import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isPast, isToday } from 'date-fns';
import { Calendar, Clock, User, LogOut, Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';

function PatientPortalDashboard() {
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [showBookModal, setShowBookModal] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookingForm, setBookingForm] = useState({
    practitionerId: '',
    date: '',
    slot: null,
    type: 'follow_up',
    notes: ''
  });
  const [bookingLoading, setBookingLoading] = useState(false);

  const token = localStorage.getItem('patientToken');

  useEffect(() => {
    if (!token) {
      navigate('/portal/login');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [meRes, apptRes, practRes] = await Promise.all([
        fetch('/api/patient-portal/me', { headers }),
        fetch('/api/patient-portal/appointments', { headers }),
        fetch('/api/patient-portal/practitioners', { headers })
      ]);

      if (!meRes.ok) {
        localStorage.removeItem('patientToken');
        navigate('/portal/login');
        return;
      }

      const meData = await meRes.json();
      const apptData = await apptRes.json();
      const practData = await practRes.json();

      setPatient(meData.patient);
      setAppointments(apptData.appointments || []);
      setPractitioners(practData.practitioners || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSlots = async (practitionerId, date) => {
    if (!practitionerId || !date) return;
    try {
      const res = await fetch(`/api/patient-portal/available-slots?practitionerId=${practitionerId}&date=${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAvailableSlots(data.slots || []);
    } catch (error) {
      setAvailableSlots([]);
    }
  };

  useEffect(() => {
    if (bookingForm.practitionerId && bookingForm.date) {
      fetchSlots(bookingForm.practitionerId, bookingForm.date);
    }
  }, [bookingForm.practitionerId, bookingForm.date]);

  const handleBook = async () => {
    if (!bookingForm.slot) {
      toast.error('Please select a time slot');
      return;
    }
    setBookingLoading(true);
    try {
      const res = await fetch('/api/patient-portal/book', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          practitionerId: bookingForm.practitionerId,
          date: bookingForm.date,
          startTime: bookingForm.slot.start,
          endTime: bookingForm.slot.end,
          type: bookingForm.type,
          notes: bookingForm.notes
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast.success('Appointment booked!');
      setShowBookModal(false);
      setBookingForm({ practitionerId: '', date: '', slot: null, type: 'follow_up', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBookingLoading(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) return;
    try {
      const res = await fetch(`/api/patient-portal/appointments/${id}/cancel`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to cancel');
      toast.success('Appointment cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('patientToken');
    localStorage.removeItem('patientData');
    navigate('/portal/login');
  };

  const upcomingAppointments = appointments.filter(a => 
    a.status !== 'cancelled' && !isPast(new Date(a.appointmentDate + 'T' + a.endTime))
  );
  const pastAppointments = appointments.filter(a => 
    a.status === 'cancelled' || isPast(new Date(a.appointmentDate + 'T' + a.endTime))
  );

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      <header style={{ background: 'linear-gradient(135deg, #1a1f3c 0%, #0d1025 100%)', padding: '1rem 2rem', color: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ margin: 0, fontSize: '1.5rem' }}>
            <span style={{ color: '#00D4AA' }}>Physio</span>Pro
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span>Welcome, {patient?.firstName}</span>
            <button onClick={handleLogout} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', padding: '0.5rem 1rem', borderRadius: '0.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h2 style={{ margin: 0 }}>My Appointments</h2>
          <button
            onClick={() => setShowBookModal(true)}
            style={{
              background: 'linear-gradient(135deg, #00D4AA 0%, #0066FF 100%)',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Plus size={18} /> Book Appointment
          </button>
        </div>

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
          <button
            onClick={() => setActiveTab('upcoming')}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontWeight: 500,
              background: activeTab === 'upcoming' ? '#0066FF' : '#e9ecef',
              color: activeTab === 'upcoming' ? 'white' : '#333'
            }}
          >
            Upcoming ({upcomingAppointments.length})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontWeight: 500,
              background: activeTab === 'history' ? '#0066FF' : '#e9ecef',
              color: activeTab === 'history' ? 'white' : '#333'
            }}
          >
            History ({pastAppointments.length})
          </button>
        </div>

        <div style={{ display: 'grid', gap: '1rem' }}>
          {(activeTab === 'upcoming' ? upcomingAppointments : pastAppointments).length === 0 ? (
            <div style={{ background: 'white', padding: '3rem', borderRadius: '1rem', textAlign: 'center', color: '#666' }}>
              {activeTab === 'upcoming' ? 'No upcoming appointments. Book one now!' : 'No past appointments.'}
            </div>
          ) : (
            (activeTab === 'upcoming' ? upcomingAppointments : pastAppointments).map(apt => (
              <div key={apt.id} style={{ background: 'white', padding: '1.5rem', borderRadius: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <Calendar size={16} style={{ color: '#0066FF' }} />
                    <strong>{format(new Date(apt.appointmentDate), 'EEEE, dd MMMM yyyy')}</strong>
                    {isToday(new Date(apt.appointmentDate)) && (
                      <span style={{ background: '#00D4AA', color: 'white', padding: '0.2rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem' }}>Today</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', color: '#666' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Clock size={14} /> {apt.startTime?.substring(0, 5)} - {apt.endTime?.substring(0, 5)}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <User size={14} /> {apt.practitioner?.firstName} {apt.practitioner?.lastName}
                    </span>
                  </div>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#888' }}>
                    {apt.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  {apt.status === 'cancelled' ? (
                    <span style={{ background: '#fee2e2', color: '#dc2626', padding: '0.5rem 1rem', borderRadius: '0.5rem', fontSize: '0.875rem' }}>Cancelled</span>
                  ) : activeTab === 'upcoming' && (
                    <button
                      onClick={() => handleCancel(apt.id)}
                      style={{ background: '#fee2e2', color: '#dc2626', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      {showBookModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000 }} onClick={() => setShowBookModal(false)}>
          <div style={{ background: 'white', borderRadius: '1rem', padding: '2rem', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Book Appointment</h2>
              <button onClick={() => setShowBookModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Practitioner</label>
              <select
                value={bookingForm.practitionerId}
                onChange={(e) => setBookingForm({ ...bookingForm, practitionerId: e.target.value, slot: null })}
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '0.5rem', fontSize: '1rem' }}
              >
                <option value="">Select a practitioner</option>
                {practitioners.map(p => (
                  <option key={p.id} value={p.id}>{p.firstName} {p.lastName} - {p.specialty}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Date</label>
              <input
                type="date"
                value={bookingForm.date}
                min={format(new Date(), 'yyyy-MM-dd')}
                onChange={(e) => setBookingForm({ ...bookingForm, date: e.target.value, slot: null })}
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '0.5rem', fontSize: '1rem' }}
              />
            </div>

            {bookingForm.practitionerId && bookingForm.date && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Available Times</label>
                {availableSlots.length === 0 ? (
                  <p style={{ color: '#666' }}>No available slots for this date</p>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '0.5rem' }}>
                    {availableSlots.map((slot, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setBookingForm({ ...bookingForm, slot })}
                        style={{
                          padding: '0.75rem',
                          border: bookingForm.slot?.start === slot.start ? '2px solid #0066FF' : '1px solid #ddd',
                          borderRadius: '0.5rem',
                          background: bookingForm.slot?.start === slot.start ? '#e8f0fe' : 'white',
                          cursor: 'pointer',
                          fontWeight: bookingForm.slot?.start === slot.start ? 600 : 400
                        }}
                      >
                        {slot.start}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Appointment Type</label>
              <select
                value={bookingForm.type}
                onChange={(e) => setBookingForm({ ...bookingForm, type: e.target.value })}
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '0.5rem', fontSize: '1rem' }}
              >
                <option value="initial_assessment">Initial Assessment</option>
                <option value="follow_up">Follow Up</option>
                <option value="treatment">Treatment</option>
                <option value="review">Review</option>
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Notes (optional)</label>
              <textarea
                value={bookingForm.notes}
                onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                placeholder="Any specific concerns or information..."
                rows={3}
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '0.5rem', fontSize: '1rem', resize: 'vertical' }}
              />
            </div>

            <button
              onClick={handleBook}
              disabled={!bookingForm.slot || bookingLoading}
              style={{
                width: '100%',
                padding: '0.875rem',
                background: bookingForm.slot ? 'linear-gradient(135deg, #00D4AA 0%, #0066FF 100%)' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: bookingForm.slot && !bookingLoading ? 'pointer' : 'not-allowed'
              }}
            >
              {bookingLoading ? 'Booking...' : 'Confirm Booking'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientPortalDashboard;
